import { Confirmation } from '../../../libs/gs-component-right-rail';
import Honeybadger from 'honeybadger-js';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  PhotoItemsFragment,
  useDeletePhotoTimelineItemMutation,
  usePhotoTimelineQuery,
  useUpdatePhotoTimelineItemMutation,
} from '../../../generated/graphql';
import deletePageItem from '../../../helpers/apolloCache/deletePageItem';
import { ImageError, ImageMetadata, ImageResult } from '../../../helpers/images/mediaApiUtils';
import { imageFileToResizedDataUrl, loadDataUrlToImg } from '../../../helpers/images/processImages';
import { getFileKey, useImageUploader, validateImage } from '../../../helpers/images/useImageUploader';
import { previewRoute } from '../../../helpers/routes';
import { useManageItem } from '../../../helpers/useManageItem';
import { pagesQuery } from '../../LeftContainer/graphql';
import { PageItemProps } from '../BasicItem';
import PhotoTimelineDropZone from './Dropzone';
import PhotoTimelineUpload from './Upload';

export interface PhotoUploading {
  id?: number;
  file?: File;
  key?: string;
  percentUploaded: number;
  dataUrl: string;
  error?: ImageError;
  mediaResponse?: ImageResult;
  mediaMetadata?: ImageMetadata;
}

interface UpdatePhotoProps {
  key: string;
  updates: any;
}

const mapPhotoItemsToPhotosUploading = (photoItems: PhotoItemsFragment[]): PhotoUploading[] => {
  return photoItems.map(({ id, mediaUrl, mediaApiId, height, width, date, caption, aspectRatio }) => ({
    id,
    percentUploaded: 100,
    dataUrl: mediaUrl,
    mediaResponse: {
      mediaApiId,
      height,
      width,
      description: caption || '',
      id: mediaApiId,
      mediaApiUrl: mediaUrl,
    },
    mediaMetadata: {
      date: date || '',
      caption: caption || '',
      aspectRatio,
    },
  }));
};

const comparePhotos = (firstEl: PhotoUploading, secondEl: PhotoUploading) => {
  const firstElDate = firstEl.mediaMetadata?.date || '';
  const secondElDate = secondEl.mediaMetadata?.date || '';
  if (firstElDate < secondElDate) {
    return -1;
  }

  if (firstElDate > secondElDate) {
    return 1;
  }

  return 0;
};

const PhotoTimeline = ({ onSaveHandler, onBackHandler }: PageItemProps) => {
  const history = useHistory();
  const [update, { loading: updateLoading }] = useUpdatePhotoTimelineItemMutation();
  const { imageUpload } = useImageUploader();
  const { itemId: id, pageId, isNew, trackItem } = useManageItem();
  const [photosUploading, setPhotosUploading] = useState<PhotoUploading[]>([]);
  const uploadsWithErrors = photosUploading.filter(p => p.error);
  const [destroy] = useDeletePhotoTimelineItemMutation();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>();
  const { data: timelineData, loading: isTimelineLoading } = usePhotoTimelineQuery({
    variables: {
      pageId,
      id: id!,
    },
    skip: isNew,
  });
  const { pathname } = useLocation();
  const existingPhotoItems = timelineData?.page.photoTimelineItem?.photoItems;

  useEffect(() => {
    if (existingPhotoItems) {
      const existingPhotoItemsAsUploading: PhotoUploading[] = mapPhotoItemsToPhotosUploading(existingPhotoItems);
      setPhotosUploading([...existingPhotoItemsAsUploading].sort(comparePhotos));
    }
  }, [existingPhotoItems]);

  const updatePhotoUploading = ({ key, updates }: UpdatePhotoProps) => {
    setPhotosUploading(prevPhotosUploading => {
      const update = prevPhotosUploading?.map(photo => {
        if (photo.key !== key) return photo;
        return { ...photo, ...updates };
      });
      return update;
    });
  };

  const removePhotoUploading = (key: string) => {
    setPhotosUploading(prevPhotosUploading => {
      return prevPhotosUploading.filter(p => p.key !== key);
    });
  };

  const addUploadsOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList) {
      addUploads(Array.from(fileList));
    }
  };

  const addUploads = async (fileRefs: File[]) => {
    const existingKeys = photosUploading.map(p => p.key);
    const deduped = fileRefs.filter(f => !existingKeys.includes(getFileKey(f)));
    // Check if there are any replacements for uploads that failed
    uploadsWithErrors.forEach(errorUpload => {
      const replacementUpload = fileRefs.find(f => errorUpload.key === getFileKey(f));
      if (replacementUpload && errorUpload.key) {
        removePhotoUploading(errorUpload.key);
        deduped.unshift(replacementUpload);
      }
    });

    const toUpload = await Promise.all(
      deduped.map(async f => {
        const dataUrl = await imageFileToResizedDataUrl(f);
        const img = await loadDataUrlToImg(dataUrl);
        return {
          dataUrl,
          error: validateImage(img),
          file: f,
          key: getFileKey(f),
          percentUploaded: 0,
        };
      })
    );

    setPhotosUploading(prevPhotosUploading => [...toUpload, ...prevPhotosUploading]);
    await Promise.all(
      toUpload
        .filter(u => !u.error)
        .map(async photo => {
          const mediaMetadata = {
            date: moment.utc(photo.file.lastModified).format('YYYY-MM-DD'),
          };
          const setUploadPercent = (percent: number) => {
            updatePhotoUploading({ key: photo.key, updates: { percentUploaded: percent, mediaMetadata } });
          };
          const res = await imageUpload({ file: photo.dataUrl, setUploadPercent });
          const updates = {
            error: res.error,
            mediaResponse: res.data,
            mediaMetadata,
          };
          updatePhotoUploading({ key: photo.key, updates });
          return { ...photo, ...updates };
        })
    );

    if (onSaveHandler) {
      onSaveHandler();
    }
  };

  const closeRightRail = () => {
    if (pathname.includes('/preview')) {
      history.push(previewRoute);
    } else {
      history.push('/gs/dashboard');
    }
  };

  const deleteTimeline = async () => {
    try {
      if (id) {
        await destroy({
          variables: {
            attributes: {
              pageId,
              id,
            },
          },
          update(cache) {
            deletePageItem({ cache, pageId, itemId: id });
          },
          refetchQueries: [{ query: pagesQuery }],
        });
      }
    } catch (error) {
      Honeybadger.notify(error, 'Error deleting Photo Gallery');
    }

    closeRightRail();
  };

  if (isNew && isEmpty(photosUploading)) {
    return <PhotoTimelineDropZone onBackHandler={history.goBack} addUploads={addUploads} />;
  }

  if (deleteConfirmOpen) {
    return (
      <Confirmation
        data-testid="delete-confirm"
        info={<div>Are you sure you want to delete this timeline?</div>}
        onPrimaryAction={deleteTimeline}
        onSecondaryAction={() => setDeleteConfirmOpen(false)}
        primaryText="Yes"
        question="Delete photo timeline"
        secondaryText="No"
      />
    );
  }

  return (
    <PhotoTimelineUpload
      addUploadsOnChange={addUploadsOnChange}
      id={id}
      pageId={pageId}
      timelineId={id || undefined}
      photosUploading={photosUploading}
      setPhotosUploading={setPhotosUploading}
      update={update}
      trackItem={trackItem}
      updateLoading={updateLoading}
      timelineLoading={isTimelineLoading}
      uploadsWithErrors={uploadsWithErrors}
      onDelete={() => setDeleteConfirmOpen(true)}
    />
  );
};

export default PhotoTimeline;
